import React from 'react';
import { PaginationProps } from './config';
import { ChevronLeftIcon, ChevronRightIcon } from '../IconButton/icons';

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  hasNext,
  hasPrevious,
  showPageInfo = true,
  showQuickJump = false,
  maxVisiblePages = 5,
  pageSizeOptions = [25, 50, 100],
  loading = false,
  variant = 'stack'
}) => {
  const isBar = variant === 'bar';
  // Generate visible page numbers
  const generatePageNumbers = (): number[] => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(currentPage - half, 1);
    let end = Math.min(start + maxVisiblePages - 1, totalPages);

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(end - maxVisiblePages + 1, 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const pageNumbers = generatePageNumbers();
  const showFirstEllipsis = pageNumbers[0] > 1;
  const showLastEllipsis = pageNumbers[pageNumbers.length - 1] < totalPages;

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
      onPageChange(page);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (onPageSizeChange && newPageSize !== pageSize) {
      onPageSizeChange(newPageSize);
    }
  };

  if (totalPages <= 1 && !isBar) {
    return null;
  }

  const startRecord = ((currentPage - 1) * pageSize) + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className={`pagination-container ${isBar ? 'pagination-container--bar' : ''}`.trim()}>
      {showPageInfo && (
        <div className="pagination-info">
          {!isBar && (
            <span>
              Showing {startRecord.toLocaleString()} to {endRecord.toLocaleString()} of {totalRecords.toLocaleString()} results
            </span>
          )}
          
          {onPageSizeChange && (
            <div className="page-size-selector">
              <label htmlFor="page-size">Show:</label>
              <select
                id="page-size"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                disabled={loading}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>per page</span>
            </div>
          )}
        </div>
      )}

      <div className="pagination-controls">
        {/* Previous button */}
        <button
          className="pagination-btn pagination-prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPrevious || loading}
          aria-label="Previous page"
        >
          {isBar ? <ChevronLeftIcon /> : '← Previous'}
        </button>

        {/* First page + ellipsis */}
        {showFirstEllipsis && (
          <>
            <button
              className="pagination-btn pagination-number"
              onClick={() => handlePageChange(1)}
              disabled={loading}
            >
              1
            </button>
            <span className="pagination-ellipsis">…</span>
          </>
        )}

        {/* Page numbers */}
        {pageNumbers.map(pageNum => (
          <button
            key={pageNum}
            className={`pagination-btn pagination-number ${pageNum === currentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(pageNum)}
            disabled={loading}
            aria-label={`Page ${pageNum}`}
            aria-current={pageNum === currentPage ? 'page' : undefined}
          >
            {pageNum}
          </button>
        ))}

        {/* Last page + ellipsis */}
        {showLastEllipsis && (
          <>
            <span className="pagination-ellipsis">…</span>
            <button
              className="pagination-btn pagination-number"
              onClick={() => handlePageChange(totalPages)}
              disabled={loading}
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next button */}
        <button
          className="pagination-btn pagination-next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNext || loading}
          aria-label="Next page"
        >
          {isBar ? <ChevronRightIcon /> : 'Next →'}
        </button>

        {/* Quick jump (optional) */}
        {showQuickJump && (
          <div className="pagination-jump">
            <label htmlFor="page-jump">Go to page:</label>
            <input
              id="page-jump"
              type="number"
              min={1}
              max={totalPages}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const page = parseInt(e.currentTarget.value);
                  if (page >= 1 && page <= totalPages) {
                    handlePageChange(page);
                    e.currentTarget.value = '';
                  }
                }
              }}
              disabled={loading}
              placeholder={`1-${totalPages}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination; 